import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Alert, Button, Flex } from '~/ui/components';
import { useAwaitControl } from 'react-redux-await-control';
import { getConciergeMessages } from '~/store/tickets/actions';
import { LoadingSpinner } from '~/ui/assets/icons';
import { LoadingContent, Title } from './ConciergeConversation.styled';

import { MessageType, RenderConversation } from '../Conversation/RenderConvesation';

function buildMessage(msg: any, mapped: { authorType: string; author: string }, index: number): MessageType {
  const base = {
    index,
    authorType: mapped.authorType,
    author: mapped.author,
    dateCreated: msg.createdAt,
    dateUpdated: msg.createdAt,
    media: [] as any,
  };

  switch (msg.type) {
    case 'reaction':
      return {
        ...base,
        type: 'reaction',
        body: '',
        content: { media_url: '', storage_key: '', content_type: '', emoji: msg.content?.emoji },
      };
    case 'location':
      return {
        ...base,
        type: 'location',
        body: '',
        content: {
          media_url: '',
          storage_key: '',
          content_type: '',
          latitude: msg.content?.latitude,
          longitude: msg.content?.longitude,
          name: msg.content?.name,
          address: msg.content?.address,
        },
      };
    case 'document':
      return {
        ...base,
        type: 'document',
        body: '',
        content: {
          media_url: msg.content?.media_url ?? '',
          storage_key: msg.content?.storage_key ?? '',
          content_type: msg.content?.content_type ?? '',
          filename: msg.content?.filename,
        },
      };
    case 'list':
      return {
        ...base,
        type: 'list',
        body: msg.content?.body ?? '',
        content: {
          media_url: '',
          storage_key: '',
          content_type: '',
          sections: msg.content?.sections,
        },
      };
    case 'list_reply':
      return {
        ...base,
        type: 'list_reply',
        body: '',
        content: {
          media_url: '',
          storage_key: '',
          content_type: '',
          listReplyTitle: msg.content?.title,
        },
      };
    case 'button_reply':
      return {
        ...base,
        type: 'button_reply',
        body: '',
        content: {
          media_url: '',
          storage_key: '',
          content_type: '',
          buttonReplyTitle: msg.content?.title,
        },
      };
    case 'template':
      return {
        ...base,
        type: 'template',
        body: '',
        content: {
          media_url: '',
          storage_key: '',
          content_type: '',
          templateName: msg.content?.name,
          templateParams: msg.content?.components
            ?.filter((c: any) => c.type === 'body')
            .flatMap((c: any) => c.parameters?.filter((p: any) => p.type === 'text').map((p: any) => p.text) ?? []),
        },
      };
    default:
      return {
        ...base,
        type: msg.type,
        body: msg.content?.body ?? '',
        ...(msg.content?.media_url && {
          content: {
            media_url: msg.content.media_url,
            storage_key: msg.content.storage_key,
            content_type: msg.content.content_type,
          },
        }),
      };
  }
}

const AUTHOR_MAP: Record<string, { authorType: string; author: string }> = {
  user: { authorType: 'CLIENT', author: 'Cliente' },
  agent: { authorType: 'AGENT', author: 'Atendente' },
  human_attendance: { authorType: 'AGENT', author: 'Atendente Humano' },
  system: { authorType: 'AGENT', author: 'Sistema' },
  ticket: { authorType: 'AGENT', author: 'Sistema' },
};

type ConciergeConversationType = {
  conversationId: string;
};

function ConciergeConversation({ conversationId }: ConciergeConversationType) {
  const [messages, setMessages] = useState<MessageType[]>([]);

  const conciergeMessagesControl = useAwaitControl(getConciergeMessages);
  const conciergeMessages = conciergeMessagesControl.result(conversationId);
  const conciergeMessagesLoading = conciergeMessagesControl.isRunning(conversationId);

  const seenIdsRef = useRef(new Set<string>());
  const counterRef = useRef(0);

  const getMoreMessages = useCallback(() => {
    conciergeMessagesControl.start(
      {
        conversationId,
        cursor: conciergeMessages?.meta?.nextCursor,
      },
      { actionId: conversationId },
    );
  }, [conversationId, conciergeMessages]);

  useEffect(() => {
    setMessages([]);
    seenIdsRef.current = new Set<string>();
    counterRef.current = 0;

    conciergeMessagesControl.start({ conversationId }, { actionId: conversationId });
  }, [conversationId]);

  useEffect(() => {
    if (conciergeMessages?.data) {
      setMessages((prev) => {
        const newMessages: MessageType[] = [];

        conciergeMessages.data.forEach((msg: any) => {
          if (seenIdsRef.current.has(msg.id)) return;
          seenIdsRef.current.add(msg.id);
          counterRef.current += 1;
          const mapped = AUTHOR_MAP[msg.author] || { authorType: 'AGENT', author: 'Sistema' };
          newMessages.push(buildMessage(msg, mapped, counterRef.current));
        });

        return [...prev, ...newMessages];
      });
    }
  }, [conciergeMessages]);

  useEffect(
    () => () => {
      conciergeMessagesControl.clear({ actionId: conversationId });
    },
    [conversationId],
  );

  if (conciergeMessagesLoading && !messages.length) {
    return (
      <Alert
        status="neutral"
        hideIcon
        fullWidth
        renderValue={
          <LoadingContent>
            <LoadingSpinner width={20} height={20} />
            <Title>Carregando mensagens...</Title>
          </LoadingContent>
        }
      />
    );
  }

  if (!conciergeMessagesLoading && !messages.length) {
    return (
      <Alert
        label="Não foi possível exibir o histórico do atendimento, conversa não possui mensagens."
        status="neutral"
        fullWidth
      />
    );
  }

  return (
    <>
      <RenderConversation messages={messages} conversationId={conversationId} />
      <Flex justify="center" pv={16} hidden={!conciergeMessages?.meta?.hasMore} pb={16}>
        <Button size="sm" variant="text" onClick={getMoreMessages} loading={conciergeMessagesLoading}>
          Carregar mais
        </Button>
      </Flex>
    </>
  );
}

export default ConciergeConversation;
